/**
 * Agent Prompt Utilities
 *
 * Shared utility functions for the TipTap-based Prompt Editor used across frontend and backend.
 */

import { DbEnum } from "../../../generated/prisma-enums";
import {
  AVAILABLE_VARIABLES,
  DEFAULT_DEPLOYMENT_DISPLAY_NAME,
  DEFAULT_DEPLOYMENT_TYPE,
  DEPLOYMENT_CONTEXT_DISPLAY_NAMES,
  INTEGRATION_DEPLOYMENT_MAPPINGS,
  INTEGRATION_DISPLAY_NAMES,
  PROMPT_MARK_NAMES,
  PROMPT_NODE_NAMES
} from "./agent-prompt.constants";
import type { SectionAttrs, TipTapJSONContent, VariableDefinition } from "./agent-prompt.types";

/**
 * Builds a text node holding markdown source to be emitted verbatim.
 *
 * The PromptEditor schema has no node for a GFM table (NEX-1987) or for authored
 * markup like `<context>` (NEX-1584), so both parsers keep the source text and rely
 * on the serializer to write it back untouched. The RAW mark is what tells the
 * serializer that — without it the text is indistinguishable from prose and gets
 * markdown-escaped, which re-escapes the escapes already in it and compounds on
 * every save (NEX-2564).
 *
 * Shared so the backend parser and the frontend markdown-manager cannot disagree
 * about what "raw" means — they previously carried separate copies of this handling
 * and drifted.
 */
export function createRawTextNode(text: string): TipTapJSONContent {
  return {
    type: PROMPT_NODE_NAMES.TEXT,
    text,
    marks: [{ type: PROMPT_MARK_NAMES.RAW }]
  };
}

/** True when a node is text carrying the RAW mark (markdown source, not prose). */
export function isRawTextNode(node: TipTapJSONContent): boolean {
  return (
    node.type === PROMPT_NODE_NAMES.TEXT &&
    (node.marks?.some((mark) => mark.type === PROMPT_MARK_NAMES.RAW) ?? false)
  );
}

/**
 * Helper function to get a variable definition by its name
 *
 * @param name - The variable name to look up
 * @returns The variable definition or undefined if not found
 */
export function getVariableByName(name: string): VariableDefinition | undefined {
  return AVAILABLE_VARIABLES.find((v) => v.name === name);
}

/**
 * Format variable placeholder: variableName -> {{variableName}}
 * Used in preview mode to show placeholders before backend execution
 *
 * @param name - Variable name (e.g., "conversationId")
 * @returns Formatted placeholder (e.g., "{{conversationId}}")
 */
export function formatVariablePlaceholder(name: string): string {
  return `{{${name}}}`;
}

/**
 * Get the display name for an integration type
 * Falls back to the type string if not found
 * Accepts both IntegrationType enum and string for flexibility with Prisma types
 *
 * @param type - The integration type (enum or string)
 * @returns The human-readable display name
 *
 * @example
 * getIntegrationTypeDisplayName(IntegrationType.WHATSAPP) // Returns "WhatsApp"
 * getIntegrationTypeDisplayName("TWILIO_VOICE") // Returns "Twilio Voice"
 */
export function getIntegrationTypeDisplayName(type: DbEnum.DeploymentType | string): string {
  return INTEGRATION_DISPLAY_NAMES[type as DbEnum.DeploymentType] || type;
}

/**
 * Get the display name for a deployment type (includes NEXUS + integration types)
 * Handles both IntegrationType values (SLACK, TELEGRAM, etc.) and "NEXUS" (the default deployment type)
 *
 * @param type - The deployment type
 * @returns The human-readable display name
 *
 * @example
 * getDeploymentDisplayName("NEXUS") // Returns "Nexus"
 * getDeploymentDisplayName("WHATSAPP") // Returns "WhatsApp"
 * getDeploymentDisplayName("SLACK") // Returns "Slack"
 */
export function getDeploymentDisplayName(type: string): string {
  if (type === DEFAULT_DEPLOYMENT_TYPE) {
    return DEFAULT_DEPLOYMENT_DISPLAY_NAME;
  }
  // Context key display names (EMAIL → "Email", PHONE_CALL → "Phone Call")
  if (DEPLOYMENT_CONTEXT_DISPLAY_NAMES[type]) {
    return DEPLOYMENT_CONTEXT_DISPLAY_NAMES[type];
  }
  // Fallback to integration display names for raw types
  return INTEGRATION_DISPLAY_NAMES[type as DbEnum.DeploymentType] || type;
}

/**
 * Every channel tab key the prompt editor can render a tab for, in tab-bar order.
 * Excludes `NEXUS`, which is the always-present default tab rather than a channel.
 */
export const CHANNEL_TAB_KEYS: string[] = Object.keys(DEPLOYMENT_CONTEXT_DISPLAY_NAMES).sort(
  compareDeploymentTypes
);

/**
 * Whether `type` is a channel the editor knows how to label and render.
 *
 * The set of tabs in a prompt is user-owned, not a mirror of the deployment list
 * (NEX-2818): a tab may be added by hand before its deployment exists, and one
 * deployment can imply a channel that no deployment type maps to directly (an
 * EMBED with voice calling implies PHONE_CALL). Consumers therefore keep any tab
 * whose key passes this check, and only prune keys that are outright unknown.
 */
export function isKnownChannelTabKey(type: string): boolean {
  return Object.prototype.hasOwnProperty.call(DEPLOYMENT_CONTEXT_DISPLAY_NAMES, type);
}

/**
 * Type guard to check if a string is a valid deployment type for tabs.
 * Accepts both IntegrationType values (EMBED, TELEGRAM, etc.) AND "NEXUS" (the default deployment type).
 *
 * @param value - The string to check
 * @returns True if the value is a valid deployment type
 *
 * @example
 * isValidDeploymentType("NEXUS") // Returns true
 * isValidDeploymentType("SLACK") // Returns true
 * isValidDeploymentType("INVALID") // Returns false
 */
export function isValidDeploymentType(value: string): boolean {
  return (
    value === DEFAULT_DEPLOYMENT_TYPE ||
    Object.values(DbEnum.DeploymentType).includes(value as DbEnum.DeploymentType)
  );
}

/**
 * Format placeholder for AI output: text -> [text]
 * Used when converting TipTap JSON to AI-consumable text
 *
 * Placeholders are inline markers that appear as badges in the editor
 * and render as [text] in the final AI output.
 *
 * @param text - Placeholder text (e.g., "customer name")
 * @returns Formatted placeholder (e.g., "[customer name]")
 */
export function formatPlaceholderOutput(text: string): string {
  return `[${text}]`;
}

/**
 * Format collection mention for output: name -> %%name%%
 *
 * @param name - Collection name
 * @returns Formatted collection mention
 */
export function formatCollectionMention(name: string): string {
  return `%%${name}%%`;
}

/**
 * Format skill mention for output: name -> @name
 * Name is expected to already be a valid slug.
 *
 * @param name - Skill name (already a valid slug)
 * @returns Formatted skill mention
 */
export function formatSkillMention(name: string): string {
  return `@${name}`;
}

/**
 * Gets the text representation of a mention-type TipTap node.
 * Used for extracting readable text from selections containing mentions.
 *
 * This is the single source of truth for how mention nodes render as text.
 * Used by:
 * - Cue quote extraction (Cmd+., bubble menu "Chat with Cue")
 * - Markdown serializer
 * - Any other place that needs mention text
 *
 * @param nodeType - The TipTap node type name
 * @param attrs - The node's attributes
 * @returns The text representation, or null if not a mention type
 *
 * @example
 * getMentionNodeText("skill-mention", { name: "provide_meal" }) // "@provide_meal"
 * getMentionNodeText("variable-mention", { variableName: "firstName" }) // "{{firstName}}"
 * getMentionNodeText("placeholder-mention", { text: "customer name" }) // "[customer name]"
 * getMentionNodeText("collection-mention", { name: "My Docs", displayName: "My Documents" }) // "%%My Documents%%"
 * getMentionNodeText("paragraph", {}) // null (not a mention)
 */
export function getMentionNodeText(
  nodeType: string,
  attrs: Record<string, unknown>
): string | null {
  switch (nodeType) {
    case PROMPT_NODE_NAMES.SKILL_MENTION: {
      const name = (attrs.name as string) || "";
      return name ? formatSkillMention(name) : null;
    }
    case PROMPT_NODE_NAMES.VARIABLE_MENTION: {
      const variableName = (attrs.variableName as string) || "";
      return variableName ? formatVariablePlaceholder(variableName) : null;
    }
    case PROMPT_NODE_NAMES.PLACEHOLDER_MENTION: {
      const text = (attrs.text as string) || "";
      return text ? formatPlaceholderOutput(text) : null;
    }
    case PROMPT_NODE_NAMES.COLLECTION_MENTION: {
      const name = (attrs.displayName as string) || (attrs.name as string) || "";
      return name ? formatCollectionMention(name) : null;
    }
    default:
      return null;
  }
}

/**
 * Converts a skill label to a valid function name (slug format).
 *
 * This is used by:
 * - Backend: To create AI function names from AgentToolConfig labels
 * - Frontend: Markdown serializer/parser for skill mentions
 *
 * The slugified name ensures:
 * - Only alphanumeric characters and underscores
 * - No consecutive underscores
 * - No leading/trailing underscores
 *
 * @param label - The skill label (e.g., "Provide meal", "My Cool Skill!")
 * @returns Slugified name (e.g., "provide_meal", "my_cool_skill")
 *
 * @example
 * slugifySkillLabel("Provide meal2")    // "provide_meal2"
 * slugifySkillLabel("Xada xaxw")        // "xada_xaxw"
 * slugifySkillLabel("My Cool Skill!")   // "my_cool_skill"
 * slugifySkillLabel("  Test  Skill  ")  // "test_skill"
 * slugifySkillLabel("365 Calendar")     // "365_calendar"
 * slugifySkillLabel("___test___")       // "test"
 */
export function slugifySkillLabel(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, "_") // Replace non-alphanumeric (except _) with _
    .replace(/_+/g, "_") // Collapse consecutive underscores
    .replace(/^_|_$/g, ""); // Remove leading/trailing underscores
}

/** Matches a valid skill slug: lowercase alphanumeric, underscores, no leading/trailing/consecutive underscores */
export const VALID_SKILL_SLUG_PATTERN = /^[a-z0-9](_?[a-z0-9])*$/;

/**
 * Build the auto-generated tool name that the voice/chat runtime registers
 * for a Knowledge-tab collection. The runtime adapter
 * (`CallAgentContextAdapter.buildCollectionToolMap`) registers each
 * collection as a tool named `search_${slugifySkillLabel(collection.name)}`.
 *
 * Used by `resolveSkillSlug` so a user typing `@search_faq_fr` against a
 * collection named "FAQ Fr" resolves correctly at parse time instead of
 * being stamped `isNotFound: true`.
 */
export function buildCollectionToolSlug(collectionName: string): string {
  return `search_${slugifySkillLabel(collectionName)}`;
}

/**
 * Lookup match for a parsed `@slug` token. The parser does not need the full
 * `SkillDefinition` shape — only whether the slug resolves to *something*
 * known on the agent, with enough metadata to fill the mention node attrs.
 */
export type SkillSlugResolution =
  | { kind: "skill"; skillId: string; name: string; isActive: boolean }
  | { kind: "collection-tool"; collectionId: string; name: string; isActive: boolean }
  | { kind: "unknown"; name: string };

/**
 * Resolve a parsed `@slug` token against the agent's live toolset.
 *
 * Two sources are checked, in order:
 *   1. AgentToolConfig labels (slugified) — explicit skills the user attached.
 *   2. Knowledge-tab collections — auto-registered as `search_<slug>` at runtime.
 *
 * If neither source matches, returns `{ kind: "unknown" }`. Callers that want
 * a fail-soft behavior should still emit the mention literally — the runtime
 * converter no longer blanks unknown mentions.
 *
 * @param skillSlug   The slug captured from `@<slug>` in the source markdown.
 * @param skills      AgentToolConfig labels available on the agent (already
 *                    slugified via `slugifySkillLabel` upstream so the lookup
 *                    is direct).
 * @param collections Knowledge-tab collections attached to the agent.
 */
export function resolveSkillSlug(
  skillSlug: string,
  skills: ReadonlyArray<{ id: string; name: string; isActive: boolean }>,
  collections: ReadonlyArray<{ id: string; name: string; isActive?: boolean }>
): SkillSlugResolution {
  const skill = skills.find((s) => s.name === skillSlug);
  if (skill) {
    return {
      kind: "skill",
      skillId: skill.id,
      name: skill.name,
      isActive: skill.isActive
    };
  }

  // Collection-tool resolution. `slugifySkillLabel` is lossy — "FAQ Fr" and
  // "FAQ-Fr" both produce "faq_fr" → tool slug "search_faq_fr". When the agent
  // has two collections that collide on the slugified tool name, we cannot
  // tell which one the author meant, so we refuse to resolve rather than pick
  // the first match and silently route the LLM to the wrong knowledge base.
  const matchingCollections = collections.filter(
    (c) => buildCollectionToolSlug(c.name) === skillSlug
  );
  const [collection] = matchingCollections;
  if (matchingCollections.length === 1 && collection) {
    return {
      kind: "collection-tool",
      collectionId: collection.id,
      name: skillSlug,
      isActive: collection.isActive !== false
    };
  }

  return { kind: "unknown", name: skillSlug };
}

/**
 * Maps Prisma IntegrationType to deployment type strings
 * Enables deployment-specific prompt content and formatting
 *
 * @param integrationType - The integration type from the database (accepts string for flexibility with Prisma)
 * @returns The deployment type string ("WHATSAPP", "EMAIL", "SMS", etc.) or "NEXUS" as default
 *
 * @example
 * mapIntegrationTypeToDeployment("WHATSAPP") // Returns "WHATSAPP"
 * mapIntegrationTypeToDeployment("OUTLOOK") // Returns "EMAIL"
 * mapIntegrationTypeToDeployment("TWILIO") // Returns "SMS"
 * mapIntegrationTypeToDeployment("EMBED") // Returns "NEXUS" (Embed uses Nexus prompt content)
 * mapIntegrationTypeToDeployment(null) // Returns "NEXUS"
 */
export function mapIntegrationTypeToDeployment(integrationType: string | null): string {
  if (!integrationType) return DEFAULT_DEPLOYMENT_TYPE;
  return (
    INTEGRATION_DEPLOYMENT_MAPPINGS[
      integrationType as keyof typeof INTEGRATION_DEPLOYMENT_MAPPINGS
    ] ?? DEFAULT_DEPLOYMENT_TYPE
  );
}

/**
 * Comparator for deployment type strings: NEXUS sorts first, rest alphabetically.
 */
export function compareDeploymentTypes(a: string, b: string): number {
  if (a === b) return 0;
  if (a === DEFAULT_DEPLOYMENT_TYPE) return -1;
  if (b === DEFAULT_DEPLOYMENT_TYPE) return 1;
  return a.localeCompare(b);
}

/**
 * Returns true if the variable is available in the given deployment context.
 * Variables with no `deployments` field are available everywhere.
 *
 * @param variable - The variable definition to check
 * @param deployment - The deployment context key (e.g., "NEXUS", "WHATSAPP", "EMAIL")
 * @returns True if the variable can be used in the given deployment
 */
export function isVariableAvailableInDeployment(
  variable: VariableDefinition,
  deployment: string
): boolean {
  if (!variable.deployments || variable.deployments.length === 0) return true;
  return variable.deployments.includes(deployment);
}

// ============================================================================
// Section Creation Utilities
// ============================================================================

/**
 * Options for creating a section JSON structure
 */
export interface CreateSectionJsonOptions {
  /** Optional text for the section title. Empty string or undefined = empty content array */
  title?: string;
  /** Content for the default (NEXUS) tab. Defaults to [{ type: "paragraph" }] */
  tabContent?: TipTapJSONContent[];
  /** Optional attrs to override defaults (id is NOT set - consumers handle it) */
  attrs?: Partial<Omit<SectionAttrs, "id">>;
}

/**
 * Creates a section JSON structure for TipTap.
 *
 * Every section has a tabContainer with at least the NEXUS tab.
 * This is the single source of truth for section structure across FE and BE.
 *
 * Note: `id` is NOT set here. Consumers should:
 * - Frontend: Let UniqueID extension generate it automatically
 * - Backend: Add id via spread: { ...createSectionJson(...), attrs: { ...attrs, id: randomUUID() } }
 *
 * @param options - Optional configuration for the section
 * @returns Section JSON structure compatible with TipTap
 *
 * @example
 * // Empty section (FE - id generated by UniqueID)
 * const section = createSectionJson();
 *
 * @example
 * // Section with title and content (BE - add id manually)
 * const section = createSectionJson({
 *   title: "Role description",
 *   tabContent: [{ type: "paragraph", content: [{ type: "text", text: "Hello" }] }]
 * });
 * // Add id for backend usage:
 * section.attrs = { ...section.attrs, id: randomUUID() };
 */
export function createSectionJson(options?: CreateSectionJsonOptions): TipTapJSONContent {
  const { title, tabContent, attrs } = options ?? {};

  return {
    type: PROMPT_NODE_NAMES.SECTION,
    attrs: {
      // id is NOT set - consumers handle it (FE: UniqueID extension, BE: randomUUID)
      isDeploymentSpecific: false,
      isReadOnly: false,
      isHidden: false,
      isDefaultConfigSection: false,
      ...attrs
    },
    content: [
      {
        type: PROMPT_NODE_NAMES.SECTION_TITLE,
        content: title ? [{ type: "text", text: title }] : []
      },
      {
        type: PROMPT_NODE_NAMES.TAB_CONTAINER,
        content: [
          {
            type: PROMPT_NODE_NAMES.TAB_PANEL,
            attrs: {
              tabId: crypto.randomUUID(),
              deploymentTypes: [DEFAULT_DEPLOYMENT_TYPE],
              deploymentDisplayName: DEFAULT_DEPLOYMENT_DISPLAY_NAME
            },
            content: tabContent ?? [{ type: "paragraph" }]
          }
        ]
      }
    ]
  };
}
